import { Fragment, useEffect, useState } from "react";
import { getConnectedBooking } from "api/booking/booking"; // Adjust the import path as needed
import toast from "react-hot-toast";
import {
  Transition,
  Dialog,
  TransitionChild,
  DialogPanel,
  DialogTitle,
  Button,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { encodeBase64 } from "@progress/kendo-file-saver";

import { getPrivacyPolicyContent } from "api/rental-agreement/rental-agreement";

import { useData } from "data/data-provider";
import Cancellation from "../CheckOutPage/Cancellation";
import PrivacyPolicy from "../CheckOutPage/PrivacyPolicy";
import PhoneNumberDisplay from "utils/PhoneFormat";
import {
  formatDateToHumanFirendly,
  formatTimeToHumanFirendly12Hour,
  formatTimeToHumanFirendly24Hour,
} from "utils/date-format";

const iconLocation = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.9999 12.3108C12.5794 12.3108 13.8599 11.0304 13.8599 9.45082C13.8599 7.87129 12.5794 6.59082 10.9999 6.59082C9.42036 6.59082 8.13989 7.87129 8.13989 9.45082C8.13989 11.0304 9.42036 12.3108 10.9999 12.3108Z"
      stroke="#FFFFFF"
      strokeWidth="1.5"
    />
    <path
      d="M3.31823 7.78254C5.12406 -0.155791 16.8849 -0.146624 18.6816 7.79171C19.7357 12.4484 16.8391 16.39 14.2999 18.8284C12.4574 20.6067 9.5424 20.6067 7.69073 18.8284C5.16073 16.39 2.26406 12.4392 3.31823 7.78254Z"
      stroke="#FFFFFF"
      strokeWidth="1.5"
    />
  </svg>
);
const iconCalendar = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.33325 1.83337V4.58337"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.6667 1.83337V4.58337"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.20825 8.33264H18.7916"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.25 7.79171V15.5834C19.25 18.3334 17.875 20.1667 14.6667 20.1667H7.33333C4.125 20.1667 2.75 18.3334 2.75 15.5834V7.79171C2.75 5.04171 4.125 3.20837 7.33333 3.20837H14.6667C17.875 3.20837 19.25 5.04171 19.25 7.79171Z"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.3869 12.5582H14.3951"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.3869 15.3082H14.3951"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.9958 12.5582H11.004"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.9958 15.3082H11.004"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.6032 12.5582H7.61143"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.6032 15.3082H7.61143"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const iconClock = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20.1666 11C20.1666 16.06 16.0599 20.1667 10.9999 20.1667C5.93992 20.1667 1.83325 16.06 1.83325 11C1.83325 5.94004 5.93992 1.83337 10.9999 1.83337C16.0599 1.83337 20.1666 5.94004 20.1666 11Z"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.4009 13.915L11.5592 12.2192C11.0642 11.9258 10.6609 11.22 10.6609 10.6425V6.88416"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const iconCar = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20.1666 14.137V16.9583C20.1666 17.3854 20.1666 17.599 20.0968 17.7674C20.0038 17.992 19.8253 18.1705 19.6007 18.2635C19.4322 18.3333 19.2187 18.3333 18.7916 18.3333C18.3645 18.3333 18.1509 18.3333 17.9824 18.2635C17.7579 18.1705 17.5794 17.992 17.4863 17.7674C17.4166 17.599 17.4166 17.3854 17.4166 16.9583C17.4166 16.5312 17.4166 16.3176 17.3468 16.1492C17.2538 15.9246 17.0753 15.7461 16.8507 15.6531C16.6822 15.5833 16.4687 15.5833 16.0416 15.5833H5.95825C5.53114 15.5833 5.31758 15.5833 5.14913 15.6531C4.92452 15.7461 4.74606 15.9246 4.65303 16.1492C4.58325 16.3176 4.58325 16.5312 4.58325 16.9583C4.58325 17.3854 4.58325 17.599 4.51348 17.7674C4.42044 17.992 4.24199 18.1705 4.01738 18.2635C3.84892 18.3333 3.63536 18.3333 3.20825 18.3333C2.78114 18.3333 2.56758 18.3333 2.39913 18.2635C2.17452 18.1705 1.99606 17.992 1.90303 17.7674C1.83325 17.599 1.83325 17.3854 1.83325 16.9583V14.137C1.83325 13.035 1.83325 12.4839 1.99058 11.9643C2.14791 11.4446 2.45357 10.9861 3.06489 10.0692L3.66659 9.16663L4.548 7.05124C5.23157 5.41065 5.57337 4.59035 6.26616 4.12849C6.95895 3.66663 7.84761 3.66663 9.62492 3.66663H12.3749C14.1522 3.66663 15.0409 3.66663 15.7337 4.12849C16.4265 4.59035 16.7682 5.41065 17.4519 7.05124L18.3333 9.16663L18.935 10.0692C19.5463 10.9861 19.8519 11.4446 20.0093 11.9643C20.1666 12.4839 20.1666 13.035 20.1666 14.137Z"
      stroke="#FFFFFF"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M1.83325 7.79163L3.66659 9.16663L5.28096 9.57023C5.42638 9.60653 5.57572 9.62496 5.72561 9.62496H16.2742C16.4241 9.62496 16.5734 9.60653 16.7189 9.57023L18.3333 9.16663L20.1666 7.79163"
      stroke="#FFFFFF"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M16.5 12.8334V12.8434"
      stroke="#FFFFFF"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M5.5 12.8334V12.8434"
      stroke="#FFFFFF"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

export default function ConnectedBookings({
  bookingDetails,
}: {
  bookingDetails: any;
}) {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isTotalBillModalOpen, setIsTotalBillModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  let [isTermOpen, setIsTermOpen] = useState(false);
  let [isCancellationOpen, setIsCancellationOpen] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState();
  const [cancellationPolicy, setCancellationPolicy] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { ourCompany } = useData();

  useEffect(() => {
    const fetchUserBookings = async () => {
      setLoading(true);
      try {
        const result = await getConnectedBooking(
          bookingDetails?.main_booking_uuid
            ? bookingDetails?.main_booking_uuid
            : bookingDetails?.unique_identifier,
        );
        const data = result.data || [];
        // Filter out the booking currently open on the booking detail page
        const filtered = data.filter((b: any) => {
          // match by unique_identifier or id to be safe
          if (!bookingDetails) return true;
          return (
            b?.unique_identifier !== bookingDetails?.unique_identifier &&
            b?.id !== bookingDetails?.id
          );
        });
        setBookings(filtered);
      } catch (err) {
        toast.error("Failed to fetch bookings");
      } finally {
        setLoading(false);
      }
    };

    if (bookingDetails?.main_booking_uuid || bookingDetails?.unique_identifier) {
      fetchUserBookings();
    }
  }, [bookingDetails?.main_booking_uuid, bookingDetails?.unique_identifier]);

  useEffect(() => {
    const getContent = async () => {
      setLoading(true);
      try {
        const response = await getPrivacyPolicyContent();

        setCancellationPolicy(response.data.cancellation_policy);
        setPrivacyPolicy(response.data.privacy_policy);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching content", error);
      }
    };

    getContent();
  }, []);

  const handleOpenTotalBillModal = () => {
    setIsTotalBillModalOpen(true);
  };

  const handleCloseTotalBillModal = () => {
    setIsTotalBillModalOpen(false);
  };

  function close() {
    setIsOpen(false);
  }

  const handleToggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const openRentalAgreement = async (booking: any) => {
    const encodedData = encodeBase64(bookingDetails?.customer?.email);
    const path = `${booking?.id}b6cr4${ourCompany?.company?.id}b6cr4${encodedData}`;
    let url = "";
    if (process.env.REACT_APP_ENV === "dev") {
      url = "https://dev.1now.app/pdf?agreement=";
    } else if (process.env.REACT_APP_ENV === "stage") {
      url = "https://stage.1now.app/pdf?agreement=";
    } else {
      url = "https://1now.app/pdf?agreement=";
    }
    window.open(`${url}${path}`, "_blank");
  };

  if (loading) {
    return (
      <div className=" mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-text-color">
          Connected Bookings
        </h2>
        <div className="text-center py-8">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-t-transparent border-gray-300"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className=" mx-auto pb-8">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-text-color">
          Connected Bookings
        </h2>

        {!loading && (!bookings || bookings.length === 0) ? (
          <>
            <div className="text-button-color">
              No connected bookings found.
            </div>
          </>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking: any, idx: number) => {
              const gradientId = `booking-gradient-${idx}`;
              return (
                <div
                  key={idx}
                  className={`rounded-none border-b px-4 border-secondary-100 ${
                    openIndex === idx
                      ? "bg-primary-6000 !rounded-md"
                      : "bg-transparent"
                  }`}
                >
                  <button
                    className="flex items-center justify-between w-full text-left py-4 focus:outline-none"
                    onClick={() => handleToggle(idx)}
                  >
                    <div className="flex items-center gap-3 md:gap-4 flex-1">
                      <img
                        src={
                          booking?.fleet?.first_image
                            ? booking.fleet.first_image
                            : "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
                        }
                        alt="Car"
                        className="w-[60px] h-[60px] md:w-[80px] md:h-[80px] object-cover rounded-xl"
                      />
                      <div className="flex-1">
                        <div
                          className={`text-md md:text-xl font-semibold ${
                            openIndex === idx
                              ? "text-button-color"
                              : "text-text-color"
                          }`}
                        >
                          {booking.fleet?.name}
                        </div>
                        <div
                          className={`text-sm md:text-base mt-1 ${
                            openIndex === idx
                              ? "text-button-color"
                              : "text-text-color"
                          }`}
                        >
                          Booking ID: {booking?.id}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Open booking details in the same tab
                            window.location.href = `/booking-details?id=${booking?.unique_identifier}`;
                          }}
                          className="mt-2 bg-primary-6000 border border-secondary-100 text-button-color text-xs md:text-sm font-semibold py-1.5 px-3 rounded-lg transition-colors duration-200"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                    <span className="flex-shrink-0 w-8 h-8 md:w-14 md:h-14 ml-4">
                      {openIndex === idx ? (
                        <svg
                          className="w-full h-full"
                          viewBox="0 0 59 59"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden
                        >
                          <path
                            d="M29.6429 34.9609C29.7768 34.9609 29.9275 34.894 30.0279 34.7935L37.8292 26.9922C37.9297 26.8917 37.9967 26.7411 37.9967 26.6071C37.9967 26.4732 37.9297 26.3225 37.8292 26.22L36.9922 25.385C36.8917 25.2846 36.7411 25.2176 36.6071 25.2176C36.4732 25.2176 36.3225 25.2846 36.2221 25.385L29.6429 31.9643L23.0636 25.385C22.9632 25.2846 22.8125 25.2176 22.6786 25.2176C22.5279 25.2176 22.394 25.2846 22.2935 25.385L21.4565 26.2221C21.356 26.3225 21.2891 26.4732 21.2891 26.6071C21.2891 26.7411 21.356 26.8917 21.4565 26.9922L29.2578 34.7935C29.3583 34.894 29.5089 34.9609 29.6429 34.9609Z"
                            fill="white"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-full h-full"
                          viewBox="0 0 59 59"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden
                        >
                          <path
                            d="M34.9609 29.3571C34.9609 29.2232 34.894 29.0725 34.7935 28.97L26.9922 21.1708C26.8917 21.0703 26.7411 21.0033 26.6071 21.0033C26.4732 21.0033 26.3225 21.0703 26.2221 21.1708L25.385 22.0078C25.2846 22.1083 25.2176 22.2589 25.2176 22.3929C25.2176 22.5268 25.2846 22.6775 25.385 22.7779L31.9643 29.3571L25.385 35.9364C25.2846 36.0368 25.2176 36.1875 25.2176 36.3214C25.2176 36.4721 25.2846 36.606 25.385 36.7065L26.2221 37.5435C26.3225 37.644 26.4732 37.7109 26.6071 37.7109C26.7411 37.7109 26.8917 37.644 26.9922 37.5435L34.7935 29.7422C34.894 29.6417 34.9609 29.4911 34.9609 29.3571Z"
                            fill="white"
                          />
                        </svg>
                      )}
                    </span>
                  </button>
                  {openIndex === idx && (
                    <div className="pb-6 text-button-color text-sm md:text-base !font-inter">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                        <div className="space-y-4">
                          <h3 className="font-bold text-base sm:text-lg mb-2 text-button-color">
                            Booking Details
                          </h3>
                          <div className="flex space-x-2 mb-2">
                            <span className="flex-shrink-0 self-start">
                              {iconCar}
                            </span>
                            <span className="truncate">
                              {bookingDetails?.fleet?.name ||
                                bookingDetails?.invoice?.breakdown?.fleet
                                  ?.name ||
                                "N/A"}
                            </span>
                          </div>
                          <div className="flex space-x-2 mb-2">
                            <span className="flex-shrink-0 self-start">
                              {iconLocation}
                            </span>
                            <span className="truncate">
                              Pickup:{" "}
                              {bookingDetails?.pick_up_location?.address ||
                                "N/A"}
                            </span>
                          </div>
                          <div className="flex space-x-2 mb-2">
                            <span className="flex-shrink-0 self-start">
                              {iconCalendar}
                            </span>
                            <span>
                              {formatDateToHumanFirendly(booking.pick_up_time)}{" "}
                              -{" "}
                              {formatDateToHumanFirendly(booking.drop_off_time)}
                            </span>
                          </div>
                          <div className="flex space-x-2 mb-2">
                            <span className="flex-shrink-0 self-start">
                              {iconClock}
                            </span>
                            <span>
                              {ourCompany?.default_time_format === "24_hour"
                                ? formatTimeToHumanFirendly24Hour(
                                    booking?.pick_up_time,
                                  )
                                : formatTimeToHumanFirendly12Hour(
                                    booking?.pick_up_time,
                                  )}{" "}
                              -{" "}
                              {ourCompany?.default_time_format === "24_hour"
                                ? formatTimeToHumanFirendly24Hour(
                                    booking?.drop_off_time,
                                  )
                                : formatTimeToHumanFirendly12Hour(
                                    booking?.drop_off_time,
                                  )}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-bold text-base sm:text-lg mb-2 text-button-color">
                            Renter Information
                          </h3>
                          <div>
                            {bookingDetails?.customer?.first_name
                              ? bookingDetails.customer?.first_name +
                                " " +
                                bookingDetails.customer?.last_name
                              : "N/A"}
                          </div>
                          <div>{bookingDetails.customer?.email || "N/A"}</div>
                          <div>
                            {bookingDetails.customer?.phone_no ? (
                              <PhoneNumberDisplay
                                className="text-button-color"
                                phone={bookingDetails.customer?.phone_no}
                              />
                            ) : (
                              "N/A"
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-bold text-base sm:text-lg mb-2 text-button-color">
                            Trip Info
                          </h3>
                          <div
                            className="mb-2 flex items-center justify-between cursor-pointer hover:text-button-color transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedInvoice(booking.invoice);
                              setIsTotalBillModalOpen(true);
                            }}
                          >
                            <span>Total Bill:</span>
                            <span className="font-medium text-button-color">
                              $
                              {Number(
                                booking.invoice?.total_price_without_security ||
                                  0,
                              ).toFixed(2)}
                            </span>
                          </div>

                          <div className="mb-2 flex items-center justify-between">
                            <span>Agreement</span>
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                openRentalAgreement(booking);
                              }}
                              className="text-button-color font-medium hover:underline cursor-pointer"
                            >
                              View Agreement &gt;
                            </div>
                          </div>
                          <div className="mb-2 flex items-center justify-between">
                            <span>Privacy Policy</span>
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsTermOpen(true);
                              }}
                              className="text-button-color font-medium hover:underline cursor-pointer"
                            >
                              View Policy &gt;
                            </div>
                          </div>
                          <div className="mb-2 flex items-center justify-between">
                            <span>Cancellation Policy</span>
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsCancellationOpen(true);
                              }}
                              className="text-button-color font-medium hover:underline cursor-pointer"
                            >
                              View Policy &gt;
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Transition appear show={isTotalBillModalOpen}>
        <Dialog as="div" className="relative z-50" onClose={() => {}}>
          <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                enter="ease-out duration-300"
                enterFrom="opacity-0 transform scale-95"
                enterTo="opacity-100 transform scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 transform scale-100"
                leaveTo="opacity-0 transform scale-95"
              >
                <DialogPanel className="w-full max-w-md bg-bg-color border border-secondary-100 p-6 rounded-md shadow-lg !z-50">
                  <div className="flex justify-between mb-4 border-b border-secondary-100 pb-2">
                    <DialogTitle as="h2" className="text-xl font-bold">
                      Invoice
                    </DialogTitle>
                    <button onClick={handleCloseTotalBillModal}>
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <span className="text-sm font-medium leading-5 text-left text-text-color">
                    <div className="flex flex-col space-y-4">
                      <div className="w-full mx-auto">
                        {selectedInvoice?.breakdown?.fleet &&
                          Object.keys(selectedInvoice?.breakdown.fleet).length >
                            0 && (
                            <div className="mt-4">
                              <span className="text-lg font-semibold text-text-color">
                                Trip Price
                              </span>
                              <div className="flex justify-between ">
                                <span
                                  className="truncate max-w-[60%]"
                                  title={
                                    selectedInvoice?.breakdown?.fleet?.name
                                  }
                                >
                                  {selectedInvoice?.breakdown?.fleet?.name}
                                </span>
                                <div className="flex flex-col items-end">
                                  <span className="text-xl font-medium text-text-color">
                                    $
                                    {Number(
                                      selectedInvoice?.breakdown?.fleet?.total,
                                    ).toFixed(2)}
                                  </span>
                                  <span className="text-sm text-text-color">
                                    ${selectedInvoice?.breakdown?.fleet?.rate}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        {selectedInvoice?.breakdown?.locations.length > 0 && (
                          <div className="mt-4">
                            <span className="text-lg font-semibold text-text-color">
                              Location Total
                            </span>
                            {selectedInvoice?.breakdown?.locations?.map(
                              (location: any) => (
                                <>
                                  <div className="flex justify-between ">
                                    <span
                                      className="truncate max-w-[60%]"
                                      title={location.name}
                                    >
                                      {location.name}
                                    </span>
                                    <div className="flex flex-col items-end">
                                      <span className="text-xl font-medium text-text-color">
                                        ${Number(location?.total).toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </>
                              ),
                            )}
                          </div>
                        )}

                        {selectedInvoice?.breakdown?.insurance.length > 0 && (
                          <div className="mt-4">
                            <span className="text-lg font-semibold text-text-color">
                              Insurance Total
                            </span>

                            {selectedInvoice?.breakdown?.insurance?.map(
                              (insurance: any) => (
                                <>
                                  <div className="flex justify-between ">
                                    <span
                                      className="truncate max-w-[60%]"
                                      title={insurance?.name}
                                    >
                                      {insurance?.name}
                                    </span>
                                    <div className="flex flex-col items-end">
                                      <span className="text-xl font-medium text-text-color">
                                        ${Number(insurance?.total).toFixed(2)}
                                      </span>
                                      <span className="text-sm text-text-color">
                                        {insurance?.rate}
                                      </span>
                                    </div>
                                  </div>
                                </>
                              ),
                            )}
                          </div>
                        )}

                        {selectedInvoice?.breakdown?.extras.length > 0 && (
                          <div className="mt-4">
                            <span className="text-lg font-semibold text-text-color">
                              Trip Extras
                            </span>

                            {selectedInvoice?.breakdown?.extras?.map(
                              (extra: any) => (
                                <>
                                  <div className="flex justify-between ">
                                    <span
                                      className="truncate max-w-[60%]"
                                      title={extra.name}
                                    >
                                      {extra.name}
                                    </span>
                                    <div className="flex flex-col items-end">
                                      <span className="text-xl font-medium text-text-color">
                                        ${Number(extra.total).toFixed(2)}
                                      </span>
                                      <span>${extra.rate}</span>
                                    </div>
                                  </div>
                                </>
                              ),
                            )}
                          </div>
                        )}

                        {selectedInvoice?.breakdown?.booking_fee.length > 0 && (
                          <div className="mt-4">
                            <div className="pb-4">
                              <span className="text-lg font-semibold text-text-color">
                                Booking Fee
                              </span>
                            </div>
                            {selectedInvoice?.breakdown?.booking_fee?.map(
                              (bookingFee: any) => (
                                <>
                                  <div className="flex justify-between ">
                                    <span
                                      className="truncate max-w-[60%]"
                                      title={
                                        bookingFee.description
                                          ? bookingFee.description
                                          : bookingFee.name
                                      }
                                    >
                                      {bookingFee.description
                                        ? bookingFee.description
                                        : bookingFee.name}
                                    </span>
                                    <div className="flex flex-col items-end">
                                      <span className="text-xl font-medium text-text-color">
                                        ${Number(bookingFee.total).toFixed(2)}
                                      </span>
                                      <span>
                                        {bookingFee.rate.includes("%")
                                          ? bookingFee.rate
                                          : `$${bookingFee.rate}`}
                                      </span>
                                    </div>
                                  </div>
                                </>
                              ),
                            )}
                          </div>
                        )}

                        {selectedInvoice?.breakdown?.taxes.length > 0 && (
                          <div className="mt-4">
                            <span className="text-lg font-semibold text-text-color">
                              Tax & Fee
                            </span>
                            {selectedInvoice?.breakdown?.taxes?.map(
                              (taxes: any) => (
                                <>
                                  <div className="flex justify-between ">
                                    <span
                                      className="truncate max-w-[60%]"
                                      title={
                                        taxes.description
                                          ? taxes.description
                                          : taxes.name
                                      }
                                    >
                                      {taxes.description
                                        ? taxes.description
                                        : taxes.name}
                                    </span>
                                    <div className="flex flex-col items-end">
                                      <span className="text-xl font-medium text-text-color">
                                        ${Number(taxes.total).toFixed(2)}
                                      </span>
                                      <span>
                                        {taxes.rate.includes("%")
                                          ? taxes.rate
                                          : `$${taxes.rate}`}
                                      </span>
                                    </div>
                                  </div>
                                </>
                              ),
                            )}
                          </div>
                        )}

                        {selectedInvoice?.coupon_info?.id && (
                          <div className="mt-4">
                            <span className="text-lg font-semibold text-text-color">
                              Coupon
                            </span>

                            <>
                              <div className="flex justify-between ">
                                <span
                                  className="truncate max-w-[60%]"
                                  title={selectedInvoice?.coupon_info?.name}
                                >
                                  {selectedInvoice?.coupon_info?.name}
                                </span>
                                <div className="flex flex-col items-end">
                                  <span className="text-xl font-medium text-text-color">
                                    $
                                    {Number(
                                      selectedInvoice?.coupon_discount || 0,
                                    ).toFixed(2)}
                                  </span>
                                  <span>
                                    {selectedInvoice?.coupon_info?.coupon_type.includes(
                                      "percentage",
                                    )
                                      ? selectedInvoice?.coupon_info?.amount +
                                        "%"
                                      : `$${selectedInvoice?.coupon_info?.amount}`}
                                  </span>
                                </div>
                              </div>
                            </>
                          </div>
                        )}

                        {(selectedInvoice?.breakdown?.discount &&
                          Object.keys(selectedInvoice?.breakdown?.discount)
                            .length > 0) ||
                        selectedInvoice?.discount !== "0.00" ? (
                          <>
                            {selectedInvoice?.breakdown?.discount &&
                            Object.keys(selectedInvoice?.breakdown?.discount)
                              .length > 0 ? (
                              <div className="mt-4">
                                <div className="flex justify-between ">
                                  <span className="text-lg font-semibold text-text-color">
                                    Discount
                                  </span>

                                  <div className="flex flex-col items-end">
                                    <span className="text-xl font-medium text-text-color">
                                      -$
                                      {Math.abs(
                                        Number(
                                          Number(
                                            selectedInvoice?.breakdown?.discount
                                              ?.total,
                                          ).toFixed(2),
                                        ),
                                      )}
                                    </span>
                                    <span>
                                      {
                                        selectedInvoice?.breakdown?.discount
                                          ?.rate
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              selectedInvoice?.discount !== "0.00" && (
                                <>
                                  <div className="mt-4">
                                    <div className="flex justify-between ">
                                      <span className="text-lg font-normal text-text-color">
                                        Discount
                                      </span>
                                      <span className="text-xl font-medium text-text-color">
                                        -$
                                        {Math.abs(
                                          Number(
                                            Number(
                                              selectedInvoice?.discount || 0,
                                            ).toFixed(2),
                                          ),
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </>
                              )
                            )}
                          </>
                        ) : null}
                        {selectedInvoice?.breakdown?.other?.length > 0 && (
                          <div className="mt-4">
                            <span className="text-lg font-semibold text-text-color">
                              Others
                            </span>
                            {selectedInvoice?.breakdown?.other?.map(
                              (other: any) => (
                                <div
                                  key={other.name}
                                  className="flex justify-between"
                                >
                                  <span
                                    className="truncate max-w-[60%]"
                                    title={other.name}
                                  >
                                    {other.name}
                                  </span>
                                  <div className="flex flex-col items-end">
                                    <span className="text-xl font-medium text-text-color">
                                      ${Number(other.total).toFixed(2)}
                                    </span>
                                    <span>${other.rate}</span>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        )}

                        <div className="mt-8 border-secondary-100 pt-4">
                          <div className="flex justify-between ">
                            <div>
                              <span className="text-lg font-semibold text-text-color">
                                Total Rental Price
                              </span>
                              <p className="mt-1 text-sm text-text-color">
                                Overall price rental
                              </p>
                            </div>
                            <div>
                              <span className="text-xl font-medium text-text-color">
                                $
                                {Number(
                                  selectedInvoice?.total_price_without_security,
                                ).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {selectedInvoice?.security_deposit_total !== 0 && (
                          <div className="mt-4">
                            <div className="flex justify-between ">
                              <span className="text-lg font-semibold text-text-color">
                                Refundable Deposit
                              </span>
                              <span className="text-xl font-medium text-text-color">
                                ${selectedInvoice?.security_deposit_total}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </span>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Transition appear show={isTermOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => {
            setIsTermOpen(false);
          }}
          static={true}
        >
          <div className="fixed inset-0 bg-neutral-100 dark:bg-neutral-900">
            <div className="flex h-full">
              <TransitionChild
                as={Fragment}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4">
                      <button
                        className="focus:outline-none focus:ring-0"
                        onClick={close}
                      >
                        <XMarkIcon
                          onClick={() => {
                            setIsTermOpen(false);
                          }}
                          className="w-5 h-5 text-black dark:text-button-color"
                        />
                      </button>
                    </div>

                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-white dark:bg-neutral-800 z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          <PrivacyPolicy terms={privacyPolicy} />
                        </div>
                      </div>
                    </div>
                  </>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Transition appear show={isCancellationOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => {
            setIsCancellationOpen(false);
          }}
          static={true}
        >
          <div className="fixed inset-0 bg-neutral-100 dark:bg-neutral-900">
            <div className="flex h-full">
              <TransitionChild
                as={Fragment}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4 z-0">
                      <button
                        className="focus:outline-none focus:ring-0"
                        onClick={close}
                      >
                        <XMarkIcon
                          onClick={() => {
                            setIsCancellationOpen(false);
                          }}
                          className="w-5 h-5 text-black dark:text-button-color"
                        />
                      </button>
                    </div>

                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-white dark:bg-neutral-800 z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          <Cancellation terms={cancellationPolicy} />
                        </div>
                      </div>
                    </div>
                  </>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
